import {
  Flex,
  FormFeedback,
  ItemDragAndDropSelector,
  Select,
  Text,
  useConfiguredForm,
} from '@wyz/ui';
import { get, isNil } from 'lodash-es';
import { Form } from '@wyz/ui';
import { Trans, useI18n, useLocale } from '~/common/context';
import { useFormContext } from 'react-hook-form';
import {
  IAnonymizedSupplierPlatform,
  ICountry,
  ISupplierTemplateForm,
} from '~/connect/types';
import { TranslationKeys } from '~/common/types';
import { useState } from 'react';
import type { IOptionItem } from '@wyz/types';
import { LocaleUtils } from '@wyz/utils';

type Props = {
  name: string;
  supplierIndex: number;
  suppliers: IAnonymizedSupplierPlatform[];
  countries: ICountry[];
};

function BestSourcingSupplierSelector({
  name,
  supplierIndex,
  suppliers,
  countries,
}: Props) {
  const { t } = useI18n();
  const locale = useLocale();
  const methods = useFormContext<ISupplierTemplateForm>();
  const { mode } = useConfiguredForm();
  const isEditMode = mode === 'WRITE';
  const [search, setSearch] = useState<string>('');
  const [countryId, setCountryId] = useState<number | undefined>(undefined);

  const withRanking = !methods.watch(
    `suppliers.${supplierIndex}.bestPriceDelay`,
  );

  return (
    <div
      className='rounded bg-light d-flex flex-column'
      style={{
        height: isEditMode ? 500 : 200,
        width: isEditMode ? '48%' : '50%',
      }}
    >
      <div className='bg-info-text text-center py-2 rounded-top'>
        <Text weight='semi-bold' color='white'>
          <Trans i18nKey='app.supplier_template.form.suppliers.ranking_title' />
        </Text>
      </div>
      <div className='p-2'>
        <FormFeedback invalid id={`${name}.error`}>
          <Trans
            i18nKey={
              (
                get(methods.formState.errors, name) as
                  | { message: string }
                  | undefined
              )?.message as keyof TranslationKeys
            }
          />
        </FormFeedback>
      </div>
      <Flex direction='column' className='p-2 flex-1 overflow-y-auto'>
        {isEditMode && (
          <Flex className='gap-2'>
            <div className='w-50'>
              <Text className='mt-2'>
                <Trans i18nKey='app.common.country' />
              </Text>
              <Select<false, IOptionItem<number>>
                name='country'
                onChange={(option) => setCountryId(option?.value ?? undefined)}
                options={countries.map((country) => ({
                  label:
                    LocaleUtils.getHumanReadableLabel({
                      userLocale: locale.locale,
                      locale: country.isoCode,
                      fallback: country.name,
                      options: { type: 'region' },
                    }) ?? country.name,
                  value: parseInt(country.externalId),
                }))}
                isClearable
              />
            </div>
            <div className='w-50'>
              <Text color='secondary' className='mt-2'>
                <Trans i18nKey='app.common.buttons.search' />
              </Text>
              <Form.Control
                type='text'
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Flex>
        )}
        <ItemDragAndDropSelector
          name={name}
          items={suppliers
            .filter(({ supplierPlatform, deliveryCountryIds }) => {
              const countryCondition =
                isNil(countryId) ||
                deliveryCountryIds.includes(countryId) ||
                supplierPlatform.supplier.deliveryCountryId === countryId;

              return (
                countryCondition &&
                supplierPlatform.supplier.name
                  .toLowerCase()
                  .includes(search.trim().toLowerCase())
              );
            })
            .map(({ supplierPlatform }) => ({
              label: supplierPlatform.supplier.name,
              value: supplierPlatform.supplier.id,
            }))}
          availableItemsLabel={t(
            'app.supplier_template.form.suppliers.available',
          )}
          selectedItemsLabel={t(
            'app.supplier_template.form.suppliers.selected',
          )}
          ranking={{ enabled: withRanking, maxItemsByRank: 1 }}
          translate={t}
        />
      </Flex>
    </div>
  );
}

export default BestSourcingSupplierSelector;
